import { Divider, Stack, Typography } from '@mui/material';
import type { Provider } from '@repo/api-gw-sdk';

import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { dayjs, DATE_FORMAT, TIME_FORMAT } from '@/utils/dayjs';

import { SnapshotIndicator } from './SnapshotIndicator';

import { AccountAsVaultTag, VaultTag } from '../vaults/vaultTag';

export const SnapshotToolbar = ({
  pointInTime,
  restoreToTime,
  vaultId,
  accessDeniedItemsCount,
  hideAccessDeniedItemsCount,
  supportPiTR: isPiTR = false,
  backupVaultInfo,
}: {
  pointInTime?: Date | null;
  restoreToTime?: Date | null;
  vaultId?: string | null;
  accessDeniedItemsCount?: number;
  hideAccessDeniedItemsCount?: boolean;
  supportPiTR?: boolean;
  backupVaultInfo?: {
    cloudProvider: Provider;
    region: string;
  };
}) => {
  const { vaults } = useBackupVaults();

  const vault = vaults.find((v) => v.id === vaultId);

  // snapshot is either stored in an Eon vault or at the source account
  if (!vault && !backupVaultInfo) {
    return null;
  }

  const time = restoreToTime || pointInTime;

  return (
    <Stack
      sx={{
        background: 'var(--mui-palette-background-default)',
        margin: '0px 40px',
        padding: '16px 24px',
        borderRadius: '4px',
      }}
      divider={<Divider className='my-[12px]' />}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography>{isPiTR ? 'Recovery point' : 'Snapshot time'}</Typography>
        <Stack direction='row' alignItems='center' gap='12px'>
          <SnapshotIndicator
            backgroundColor={vault?.backgroundColor || null}
            isNative={!vault}
            isPartial={!!accessDeniedItemsCount}
          />
          {(time || isPiTR) && (
            <>
              {' '}
              <Typography>
                {time
                  ? `${dayjs.utc(time).format(DATE_FORMAT)} at ${dayjs.utc(time).format(TIME_FORMAT)} UTC`
                  : isPiTR
                    ? 'Latest available'
                    : '--'}
              </Typography>
              <Divider orientation='vertical' flexItem className='mx-[12px]' />
            </>
          )}
          <Typography>Stored in</Typography>
          {vault ? (
            <VaultTag vault={vault} />
          ) : (
            backupVaultInfo && (
              <AccountAsVaultTag
                cloudProvider={backupVaultInfo.cloudProvider}
                region={backupVaultInfo.region}
              />
            )
          )}
        </Stack>
      </Stack>
      {!!accessDeniedItemsCount && !hideAccessDeniedItemsCount && (
        <Typography>
          {accessDeniedItemsCount === 1
            ? 'This is a partial snapshot. 1 file wasn`t backed up.'
            : `This is a partial snapshot. ${
                accessDeniedItemsCount
              } files weren’t backed up.`}
        </Typography>
      )}
    </Stack>
  );
};
